import { ProtectedRoute } from "@src/common/ProtectedRoute";
import { UTMNavigate } from "@src/common/UTMNavigate";
import { APP_ROUTES, LOCAL_STORAGE_KEYS } from "@src/constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useComponentNameContext } from "@src/context/ComponentNameContext";
import { useInteractionGuard } from "@src/context/InteractionGuardContext";
import logger from "@src/utils/logger";
import { appendParamsToUrl } from "@src/utils/url-utils";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import * as AbyssRouterModule from "@uhg-abyss/web/ui/Router";
import type { ComponentType, FC, ReactNode } from "react";
import { Fragment, useEffect } from "react";
import { AppBody } from "../common/AppBody";
import { AppointmentConfirmed } from "./AppointmentConfirmed";
import { Contact } from "./Contact/Contact";
import { ContactUs } from "./ContactUs/ContactUs";
import { Error as ErrorPage } from "./Error";
import { FindProvider } from "./FindProvider";
import { CombinedFindProvider } from "./FindProvider/CombinedFindProvider";
import { useIsCombinedFindProviderTreatment } from "./FindProvider/combined-find-provider-experiment";
import { GetStarted } from "./GetStarted";
import { HealthCheck } from "./HealthCheck";
import { MeetTheTeam } from "./MeetTheTeam";
import { NotFound } from "./NotFound";
import { PatientInformation } from "./PatientInformation";
import { PatientScreener } from "./PatientScreener";
import { PortalRegistrationOutcome } from "./PortalRegistrationOutcome";
import { ProviderProfile } from "./ProviderProfile";
import { Refer } from "./Refer";

type RouterNamespace = {
  MetaTags?: ComponentType<Record<string, unknown>>;
  Outlet?: ComponentType<Record<string, unknown>>;
  Routes: ComponentType<Record<string, unknown>>;
  Route: ComponentType<Record<string, unknown>>;
};

const Router =
  (AbyssRouterModule as { Router?: RouterNamespace }).Router ??
  (AbyssRouterModule as unknown as RouterNamespace);

const FindProviderRoute = () => {
  const isCombinedTreatment = useIsCombinedFindProviderTreatment();
  if (isCombinedTreatment) return <CombinedFindProvider />;
  return (
    <ProtectedRoute expectedRoute={APP_ROUTES.GET_STARTED}>
      <FindProvider />
    </ProtectedRoute>
  );
};

// Helper function to convert route path to component name
const getComponentNameFromPath = (pathname: string): string => {
  const pathName = pathname.startsWith("/") ? pathname.slice(1) : pathname;
  if (!pathName) return "GetStarted"; // Default for root

  return pathName
    .split("/")
    .map((segment) =>
      segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    )
    .join("/");
};

// Route Change Listener Component
const RouteChangeListener: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { setComponentName } = useComponentNameContext();
  const { endTransition } = useInteractionGuard();
  const { data, setData } = useApplicationContext();
  // Use the full location key (pathname + search + hash) so that same-pathname
  // navigations (query/hash changes) also clear the transition lock instead of
  // leaving the UI blocked until the 10 s timeout fires.
  const locationKey = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    const componentName = getComponentNameFromPath(location.pathname);
    setComponentName(componentName);
    logger.info({
      text: `Component name set to ${componentName}`,
      location: "RouteChangeListener",
    });

    // Track the last page visited for Qualtrics.
    setData(
      LOCAL_STORAGE_KEYS.LAST_PAGE_URL,
      location.pathname === APP_ROUTES.PORTAL_REGISTRATION_CALLBACK
        ? location.pathname
        : `${location.pathname}${location.search}`,
    );

    // Track the completion status for Qualtrics.
    if (data[LOCAL_STORAGE_KEYS.COMPLETION_STATUS] === undefined) {
      setData(LOCAL_STORAGE_KEYS.COMPLETION_STATUS, false);
    }

    // Clear the route-transition lock after the new route has painted.
    // Cancel the RAF on cleanup so a rapid second navigation does not call
    // endTransition from a stale frame.
    let rafId: ReturnType<typeof requestAnimationFrame>;
    rafId = requestAnimationFrame(() => {
      endTransition();
    });
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [
    locationKey,
    location.pathname,
    location.search,
    setComponentName,
    endTransition,
    setData,
    data,
  ]);

  return <Fragment>{children}</Fragment>;
};

export const Routes = () => {
  // Extract query parameters from the current URL
  const queryParams = location.search;

  return (
    <Router.Routes title="{{title}} | OBC">
      <Router.Route
        path={APP_ROUTES.ROOT}
        element={
          <RouteChangeListener>
            <AppBody />
          </RouteChangeListener>
        }
      >
        {/* Redirect the root path to /get-started with query parameters if present */}
        <Router.Route
          index
          element={
            <UTMNavigate
              to={appendParamsToUrl(
                APP_ROUTES.GET_STARTED,
                Object.fromEntries(new URLSearchParams(queryParams)),
              )}
              replace
            />
          }
        />
        <Router.Route path={`${APP_ROUTES.REFER}/:parentOrganization?`} element={<Refer />} />
        <Router.Route path={APP_ROUTES.GET_STARTED} element={<GetStarted />} />
        <Router.Route path={APP_ROUTES.MEET_THE_TEAM} element={<MeetTheTeam />} />
        <Router.Route
          path={APP_ROUTES.SCREENER}
          element={
            <ProtectedRoute expectedRoute={APP_ROUTES.GET_STARTED}>
              <PatientScreener />
            </ProtectedRoute>
          }
        />
        <Router.Route path={APP_ROUTES.FIND_PROVIDER} element={<FindProviderRoute />} />
        <Router.Route
          path={APP_ROUTES.PROVIDER_PROFILE}
          element={
            <ProtectedRoute
              expectedRoute={APP_ROUTES.FIND_PROVIDER}
              expectedRouteByProviderSelectionSource={{
                [APP_ROUTES.MEET_THE_TEAM]: APP_ROUTES.GET_STARTED,
              }}
            >
              <ProviderProfile />
            </ProtectedRoute>
          }
        />
        <Router.Route
          path={APP_ROUTES.PATIENT_INFORMATION}
          element={<UTMNavigate to={`${APP_ROUTES.PATIENT_INFORMATION}/cover-care`} replace />}
        />
        <Router.Route
          path={`${APP_ROUTES.PATIENT_INFORMATION}/:slug`}
          element={
            <ProtectedRoute expectedRoute={APP_ROUTES.PROVIDER_PROFILE}>
              <PatientInformation />
            </ProtectedRoute>
          }
        />
        <Router.Route
          path={APP_ROUTES.APPOINTMENT_CONFIRMED}
          element={
            <ProtectedRoute expectedRoute={APP_ROUTES.PATIENT_INFORMATION}>
              <AppointmentConfirmed />
            </ProtectedRoute>
          }
        />
        <Router.Route
          path={APP_ROUTES.PORTAL_REGISTRATION_COMPLETE}
          element={<PortalRegistrationOutcome outcome="completed" />}
        />
        <Router.Route
          path={APP_ROUTES.PORTAL_REGISTRATION_INVITATION_FALLBACK}
          element={<PortalRegistrationOutcome outcome="fallback" />}
        />
        <Router.Route path="*" element={<UTMNavigate to={APP_ROUTES.PAGE_NOT_FOUND} replace />} />
        <Router.Route path={APP_ROUTES.PAGE_NOT_FOUND} element={<NotFound />} />
        <Router.Route path={APP_ROUTES.CONTACT_US} element={<ContactUs />} />
        {/* TODO: Confirm if /contact-us should remain. This page may be displayed when no provider is found */}
        <Router.Route path={APP_ROUTES.ERROR} element={<ErrorPage />} />
        <Router.Route path={APP_ROUTES.HEALTH_CHECK} element={<HealthCheck />} />
        <Router.Route path={APP_ROUTES.CONTACT} element={<Contact />} />
      </Router.Route>
    </Router.Routes>
  );
};
